import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  TransferState,
  makeStateKey,
} from '@angular/core';
import { Request as ExpressRequest } from 'express';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  environmentAU,
  environmentGLOBAL,
  environmentUS,
} from 'src/environments/environment';
import {
  SERVER_ENVIRONMENT,
  STORYBLOK_PREVIEW,
} from '../token/environment.token';
import { REQUEST } from '../token/express.tokens';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  environmentLocale: string = 'default';
  environment: any; // Ensure you properly type the environment variables later

  constructor(
    @Optional() @Inject(SERVER_ENVIRONMENT) public override: string,
    @Optional() @Inject(STORYBLOK_PREVIEW) public sbpreview: string,
    @Optional() @Inject(REQUEST) private request: ExpressRequest,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient,
    private transferState: TransferState
  ) {
    const OVERRIDE_HOSTNAME = makeStateKey<string>('OVERRIDE_HOSTNAME');
    const STORYBLOK_PREVIEW = makeStateKey<string>('STORYBLOK_PREVIEW');

    // Fetch transfer state for Storyblok preview
    let serverStoryblokPreview = this.transferState.get(
      STORYBLOK_PREVIEW,
      null
    );
    if (!serverStoryblokPreview) {
      if (this.request) {
        this.transferState.set(STORYBLOK_PREVIEW, 'true');
      }
      serverStoryblokPreview = this.transferState.get(
        STORYBLOK_PREVIEW,
        'false'
      );
    }

    // Fetch transfer state for server hostname
    let serverHostname = this.transferState.get(OVERRIDE_HOSTNAME, null);
    if (!serverHostname) {
      if (this.request) {
        this.transferState.set(OVERRIDE_HOSTNAME, this.request?.hostname);
      }
      serverHostname = this.transferState.get(OVERRIDE_HOSTNAME, null);
    }

    // Check if there's a locale in the query string
    const urlSearchParams = new URLSearchParams(this.document.location?.search);

    const queryLocale =
      urlSearchParams.get('locale') ??
      urlSearchParams.get('_storyblok_lang') == 'default'
        ? 'en-au'
        : urlSearchParams.get('_storyblok_lang');

    // Override logic based on query string locale
    if (queryLocale) {
      this.setEnvironmentByLocale(
        queryLocale,
        sbpreview,
        serverStoryblokPreview
      );
    } else {
      // Fallback to hostname-based locale logic
      const hostname = this.override ?? serverHostname ?? '';
      this.setEnvironmentByHostname(hostname);
    }

    this.getUserCountry().subscribe(data => { console.log(data)})
  }

  private setEnvironmentByLocale(
    locale: string,
    sbpreview: string,
    serverStoryblokPreview: string | null
  ): void {
    switch (locale.toLowerCase()) {
      case 'en-au':
        this.environmentLocale = 'en-au';
        this.environment = environmentAU;
        break;
      case 'en-us':
        this.environmentLocale = 'en-us';
        this.environment = environmentUS;
        break;
      default:
        this.environmentLocale = 'en-gb';
        this.environment = environmentGLOBAL;
        break;
    }
    this.environment.production =
      sbpreview === 'true' ? false : serverStoryblokPreview !== 'true';
  }

  private setEnvironmentByHostname(hostname: string): void {
    if (hostname.includes('au')) {
      this.environmentLocale = 'en-au';
      this.environment = environmentAU;
    } else if (hostname.includes('us')) {
      this.environmentLocale = 'en-us';
      this.environment = environmentUS;
    } else {
      this.environmentLocale = 'en-gb';
      this.environment = environmentGLOBAL;
    }
  }

  isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  init() {}

  getWindow(): Window | null {
    return this.document?.defaultView;
  }

  getUserCountry(): Observable<any | undefined> {
    if (this.isBrowser()) {
      return this.http.get('https://ipapi.co/json/').pipe(
        map((response: any) => response),
        catchError(() => of(undefined)) // Default to 'au' if geolocation fails
      );
    }
    return of(undefined); // Default to 'au' for server-side rendering
  }

  getCountryEndpoint(countryCode: string): string {
    return (
      this.environment.countryEndpoints[countryCode] || this.environment.siteUrl
    );
  }
}
